'use client';
import { useTranslations } from 'next-intl';
import { EditPicture } from '@/app/components/icons/EditPicture';
import type { showModeType } from '@/app/components/shared/ImageUploaderWithPreview';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogOriginalClose, DialogTrigger } from '@/app/components/ui/dialog';
import type { ImagePrompt } from '@/app/types';
import { cn } from '@/app/utils';

export const ImageOperationDialog = ({
  image,
  showMode = 'cropped',
  operations,
  handleRemoveImage,
  triggerClassName,
}: {
  image: ImagePrompt;
  showMode?: showModeType;
  operations?: {
    key: string;
    icon: React.ReactNode;
    text: string;
    onClick: () => void;
    disabled?: boolean;
  }[];
  handleRemoveImage: () => void;
  triggerClassName?: string;
}) => {
  const t = useTranslations();

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div
          className={cn(
            'absolute top-1.5 right-1.5 hidden cursor-pointer items-center justify-center rounded-4 bg-[rgba(0,0,0,.5)] p-1 text-system-white text-xl sm:flex',
            triggerClassName,
          )}
        >
          <EditPicture />
        </div>
      </DialogTrigger>
      <DialogContent variant="fullScreen">
        <div className="flex h-full flex-col pb-10">
          <div className="flex flex-1 items-center justify-center pt-11 pb-4">
            <div className="relative">
              <ImageWithPlaceholder
                src={showMode === 'cropped' ? image.selectedImagePreview : image.sourceImagePreview}
                className="max-h-[calc(100vh-168px)] max-w-full"
                alt="uploaded image"
                loading="eager"
              />
            </div>
          </div>
          <div className="flex items-center justify-center gap-4">
            <DialogOriginalClose asChild>
              <Button variant="outline" className="h-9 px-6" onClick={handleRemoveImage}>
                {t('create.createForm.upload.removeImage')}
              </Button>
            </DialogOriginalClose>
            {operations?.map((operation) => (
              <DialogOriginalClose key={operation.key} asChild>
                <Button
                  variant="primary"
                  className="h-9 px-6"
                  onClick={operation.onClick}
                  disabled={operation.disabled}
                >
                  {operation.text}
                </Button>
              </DialogOriginalClose>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
